import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';

const AgreementsListPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const [hasPackage, setHasPackage] = useState(false);
  const [hasLoan, setHasLoan] = useState(false);
  const [search, setSearch] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      let q = supabase.from('agreements').select('*').order('updated_at', { ascending: false });
      if (status !== 'all') q = q.eq('status', status);
      const { data } = await q;
      const agreements = data || [];
      // Fetch submission names for agreements missing title
      const subIds = Array.from(new Set((agreements || []).map(a => a.submission_id).filter(Boolean)));
      let subMap = {};
      if (subIds.length) {
        const { data: subs } = await supabase.from('wizard_submissions').select('id, personal_info').in('id', subIds);
        (subs || []).forEach(s => {
          const pi = s.personal_info || {};
          subMap[s.id] = pi.fullName || pi.name || `${pi.firstName || ''} ${pi.lastName || ''}`.trim();
        });
      }
      // Build item summaries
      const agIds = Array.from(new Set((agreements || []).map(a => a.id)));
      let itemsByAgreement = {};
      let nameMap = {};
      if (agIds.length) {
        const { data: items } = await supabase
          .from('agreement_items')
          .select('id, agreement_id, item_type, ref_id, title')
          .in('agreement_id', agIds);
        (items || []).forEach(i => {
          if (!itemsByAgreement[i.agreement_id]) itemsByAgreement[i.agreement_id] = [];
          itemsByAgreement[i.agreement_id].push(i);
        });
        // Resolve friendly names for packages/loans referenced by items
        const pkgIds = Array.from(new Set((items || []).filter(i => i.item_type === 'package' && i.ref_id).map(i => i.ref_id)));
        const loanIds = Array.from(new Set((items || []).filter(i => i.item_type === 'loan' && i.ref_id).map(i => i.ref_id)));
        if (pkgIds.length) {
          const { data: pkgs } = await supabase.from('packages').select('id, title, name').in('id', pkgIds);
          (pkgs || []).forEach(p => { nameMap[`package:${p.id}`] = p.title || p.name || `Package ${String(p.id).slice(0, 8)}`; });
        }
        if (loanIds.length) {
          const { data: loans } = await supabase.from('loan_options').select('id, title, provider_name').in('id', loanIds);
          (loans || []).forEach(l => { nameMap[`loan:${l.id}`] = l.title || l.provider_name || `Loan ${String(l.id).slice(0, 8)}`; });
        }
      }

      const withDisplay = (agreements || []).map(a => {
        const baseTitle = a.title || (a.submission_id ? (subMap[a.submission_id] || a.recipient_email || a.id) : a.recipient_email || a.id);
        const lead = a.submission_id ? (subMap[a.submission_id] || '-') : '-';
        const items = itemsByAgreement[a.id] || [];
        const itemLabels = items.map(i => nameMap[`${i.item_type}:${i.ref_id}`] || i.title).filter(Boolean);
        const types = new Set(items.map(i => i.item_type));
        const summary = itemLabels.length ? (itemLabels.slice(0, 3).join(' + ') + (itemLabels.length > 3 ? ` + ${itemLabels.length - 3} more` : '')) : '-';
        return {
          ...a,
          displayTitle: baseTitle,
          displayLead: lead,
          itemsSummary: summary,
          hasPackage: types.has('package'),
          hasLoan: types.has('loan'),
        };
      });
      setRows(withDisplay);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [status]);

  const filteredRows = useMemo(() => {
    const q = (search || '').toLowerCase().trim();
    return rows.filter(r => {
      if (hasPackage && !r.hasPackage) return false;
      if (hasLoan && !r.hasLoan) return false;
      if (q) {
        const hay = `${r.displayTitle || ''} ${r.displayLead || ''} ${r.itemsSummary || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, hasPackage, hasLoan, search]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div></div>;

  return (
    <div className="min-h-screen bg-gray-900 font-mono text-white p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">📑 Agreement Management</h1>
        <p className="text-gray-400">Create, manage, and track service agreements with clients</p>
      </div>

      {/* Controls */}
      <div className="bg-gray-800 rounded-lg shadow border border-gray-700 mb-6">
        <div className="p-4">
          <div className="flex gap-2 mb-4">
            <Link to="/admin/agreements/new" className="px-3 py-1.5 rounded text-sm font-medium transition-colors bg-blue-600 text-white hover:bg-blue-700">✨ New Agreement</Link>
            <Link to="/admin/agreements/templates" className="px-3 py-1.5 rounded text-sm font-medium transition-colors bg-gray-700 text-white border border-gray-600 hover:bg-gray-600">📋 Templates & Clauses</Link>
          </div>
          <div className="flex flex-wrap gap-3 items-center">
            <input
              className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent flex-1 min-w-[200px]"
              placeholder="🔍 Search title, lead, items"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="all">All statuses</option>
              {['draft', 'sent', 'accepted', 'rejected', 'cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm text-gray-300">
              <input
                type="checkbox"
                checked={hasPackage}
                onChange={(e) => setHasPackage(e.target.checked)}
                className="rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-2 focus:ring-blue-500"
              />
              <span>📦 Has Package</span>
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-300">
              <input
                type="checkbox"
                checked={hasLoan}
                onChange={(e) => setHasLoan(e.target.checked)}
                className="rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-2 focus:ring-blue-500"
              />
              <span>💰 Has Loan</span>
            </label>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-gray-800 rounded-lg shadow border border-gray-700">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr>
                <th className="bg-gray-900 p-3 border-b border-gray-700 text-xs font-medium text-gray-400 uppercase tracking-wider">Title</th>
                <th className="bg-gray-900 p-3 border-b border-gray-700 text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                <th className="bg-gray-900 p-3 border-b border-gray-700 text-xs font-medium text-gray-400 uppercase tracking-wider">Lead</th>
                <th className="bg-gray-900 p-3 border-b border-gray-700 text-xs font-medium text-gray-400 uppercase tracking-wider">Items</th>
                <th className="bg-gray-900 p-3 border-b border-gray-700 text-xs font-medium text-gray-400 uppercase tracking-wider">Total</th>
                <th className="bg-gray-900 p-3 border-b border-gray-700 text-xs font-medium text-gray-400 uppercase tracking-wider">Updated</th>
                <th className="bg-gray-900 p-3 border-b border-gray-700 text-xs font-medium text-gray-400 uppercase tracking-wider"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredRows.map(a => (
                <tr key={a.id} className="hover:bg-gray-700/50 transition-colors">
                  <td className="p-3 border-b border-gray-700 text-sm font-medium text-white">{a.displayTitle}</td>
                  <td className="p-3 border-b border-gray-700 text-sm">
                    <span className={`px-2 py-1 rounded text-xs font-medium border ${a.status === 'accepted' ? 'bg-green-900/20 text-green-400 border-green-800' :
                      a.status === 'rejected' || a.status === 'cancelled' ? 'bg-red-900/20 text-red-400 border-red-800' :
                        a.status === 'sent' ? 'bg-yellow-900/20 text-yellow-400 border-yellow-800' : 'bg-gray-700 text-gray-300 border-gray-600'}`}>
                      {a.status}
                    </span>
                  </td>
                  <td className="p-3 border-b border-gray-700 text-sm text-gray-300">{a.displayLead}</td>
                  <td className="p-3 border-b border-gray-700 text-sm text-gray-300">{a.itemsSummary}</td>
                  <td className="p-3 border-b border-gray-700 text-sm font-medium text-white">{a.total_amount} {a.currency}</td>
                  <td className="p-3 border-b border-gray-700 text-sm text-gray-400">{a.updated_at ? new Date(a.updated_at).toLocaleString() : '-'}</td>
                  <td className="p-3 border-b border-gray-700 text-sm text-right">
                    <div className="flex gap-2 justify-end">
                      <Link to={`/admin/agreements/${a.id}`} className="px-3 py-1.5 rounded text-sm font-medium transition-colors bg-blue-600 text-white hover:bg-blue-700">Open</Link>
                      {a.submission_id && (
                        <Link to={`/admin/submissions/${a.submission_id}`} className="px-3 py-1.5 rounded text-sm font-medium transition-colors bg-gray-700 text-white border border-gray-600 hover:bg-gray-600">
                          {a.displayLead || 'Lead'}
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filteredRows.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-gray-500">No agreements found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AgreementsListPage;
